import { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const empty = { name: "", company_name: "", phone: "", email: "", address: "", notes: "" };

export default function Landlords() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => client.get("/landlords").then((r) => setItems(r.data)), []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await client.put(`/landlords/${editing}`, form);
        toast.success("Landlord updated");
      } else {
        await client.post("/landlords", form);
        toast.success("Landlord added");
      }
      setForm(empty);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const edit = (l) => {
    setEditing(l.id);
    setForm({
      name: l.name || "",
      company_name: l.company_name || "",
      phone: l.phone || "",
      email: l.email || "",
      address: l.address || "",
      notes: l.notes || "",
    });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this landlord?")) return;
    await client.delete(`/landlords/${id}`);
    toast.success("Deleted");
    load();
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="landlords-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#6B7280] mb-1">Directory</div>
          <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#1E3A5F]">
            Landlords & agents
          </h1>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditing(null);
            setForm(empty);
          }}
          className="trent-btn-primary"
          data-testid="add-landlord-btn"
        >
          <Plus weight="bold" size={16} /> Add landlord
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="trent-card p-6 mb-8 grid md:grid-cols-2 gap-4" data-testid="landlord-form">
          <Field label="Name *">
            <input required value={form.name} onChange={upd("name")} className="trent-input" data-testid="landlord-name" />
          </Field>
          <Field label="Company name">
            <input value={form.company_name} onChange={upd("company_name")} className="trent-input" data-testid="landlord-company" />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={upd("phone")} className="trent-input" data-testid="landlord-phone" />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={upd("email")} className="trent-input" data-testid="landlord-email" />
          </Field>
          <Field label="Address" full>
            <input value={form.address} onChange={upd("address")} className="trent-input" data-testid="landlord-address" />
          </Field>
          <Field label="Notes" full>
            <textarea value={form.notes} onChange={upd("notes")} rows={3} className="trent-input" data-testid="landlord-notes" />
          </Field>
          <div className="md:col-span-2 flex justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="trent-btn-secondary">
              Cancel
            </button>
            <button type="submit" className="trent-btn-primary" data-testid="landlord-save-btn">
              {editing ? "Save changes" : "Add landlord"}
            </button>
          </div>
        </form>
      )}

      <div className="trent-card overflow-hidden">
        <table className="w-full text-sm" data-testid="landlords-table">
          <thead className="bg-[#F3F4F6] text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Company</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Email</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-[#6B7280] py-12">
                  No landlords yet. Add your first.
                </td>
              </tr>
            )}
            {items.map((l) => (
              <tr key={l.id} className="border-t border-[#E5E7EB]" data-testid={`landlord-row-${l.id}`}>
                <td className="px-4 py-3 font-semibold text-[#1E3A5F]">{l.name}</td>
                <td className="px-4 py-3 hidden md:table-cell">{l.company_name || "—"}</td>
                <td className="px-4 py-3 hidden md:table-cell text-[#6B7280]">{l.email || "—"}</td>
                <td className="px-4 py-3 text-[#6B7280]">{l.phone || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => edit(l)} className="p-2 hover:bg-[#F3F4F6] rounded-sm" data-testid={`edit-landlord-${l.id}`}>
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => remove(l.id)} className="p-2 hover:bg-[#FEE2E2] rounded-sm text-[#991B1B]" data-testid={`delete-landlord-${l.id}`}>
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
